/* global CartItems, CartDrawer, publish, PUB_SUB_EVENTS, fetchShopifyGraphQL, SHOPIFY_PREFIX, APPSTLE_PROPS */

const CREATE_CART_GQL = `mutation CartCreate($cartInput: CartInput!) {
  cartCreate(input: $cartInput) {
    cart {
      id
      checkoutUrl
    }
    userErrors {
      field
      message
    }
  }
}`

function getCartSectionIds(cart) {
  if (!cart || typeof cart.getSectionsToRender !== 'function') return []
  return cart
    .getSectionsToRender()
    .map(section => section.section || section.id)
    .filter(Boolean)
}

function getCartComponent() {
  const drawer = document.querySelector('cart-drawer')
  if (typeof drawer?.applyAddResponse === 'function') return drawer
  const items = document.querySelector('cart-items')
  if (typeof items?.applyAddResponse === 'function') return items
  return drawer || items || null
}

function attachCartAddPayload(cart, payload) {
  const sectionIds = getCartSectionIds(cart)
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',')
    payload.sections_url = window.location.pathname
  }
  return payload
}

async function ensureCartAddSections(cart, response) {
  if (response?.sections) return response
  const sectionIds = getCartSectionIds(cart)
  if (!sectionIds.length) return response

  const cartUrl = window.routes?.cart_url || '/cart'
  const sections = await fetch(`${cartUrl}?sections=${sectionIds.join(',')}`).then(result => result.json())
  if (!sections) return response
  if (sections.sections) return { ...response, sections: sections.sections }
  if (sections['cart-drawer'] || sections['cart-icon-bubble']) return { ...response, sections }
  return response
}

async function applyCartAddResponse(cart, response, { source, productVariantId } = {}) {
  if (!cart) {
    window.location = window.routes?.cart_url || '/cart'
    return
  }

  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source,
      productVariantId,
      cartData: response
    })
  }

  cart.classList.remove('is-empty')
  cart.querySelector('.drawer__inner')?.classList.remove('is-empty')

  const state = await ensureCartAddSections(cart, response)
  if (typeof cart.renderContents !== 'function' || !state?.sections) return

  if (window.CartPerformance?.measure) {
    window.CartPerformance.measure('add:paint-updated-sections', () => cart.renderContents(state))
    return
  }
  cart.renderContents(state)
}

function buildBuyNowAttributes(item) {
  const attributes = Object.entries(item.properties || {}).map(([key, value]) => ({
    key,
    value: value == null ? '' : String(value)
  }))
  if (!item.selling_plan || attributes.some(attribute => attribute.key === APPSTLE_PROPS.SELECTED_PLAN)) {
    return attributes
  }

  attributes.push(
    { key: APPSTLE_PROPS.SELECTED_PLAN, value: `${item.selling_plan}` },
    { key: APPSTLE_PROPS.FULFILLMENT_COUNT, value: '1' }
  )
  return attributes
}

function buildBuyNowVariables(items) {
  return {
    cartInput: {
      lines: items.map(item => {
        const attributes = buildBuyNowAttributes(item)
        return {
          quantity: item.quantity,
          merchandiseId: `${SHOPIFY_PREFIX.variant}${item.id}`,
          ...(item.selling_plan ? { sellingPlanId: `${SHOPIFY_PREFIX.sellingPlan}${item.selling_plan}` } : {}),
          ...(attributes.length ? { attributes } : {})
        }
      })
    }
  }
}

async function addItemsToCart(payload, { variantId } = {}) {
  const reportId = variantId || payload.items?.[0]?.id

  try {
    const result = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
    const response = await result.json()

    if (!result.ok || response.status) {
      window.ReportSentry?.cartAddError({ variantId: reportId, quantity: 1, response })
      return null
    }

    return response
  } catch (error) {
    window.ReportSentry?.cartAddError({
      variantId: reportId,
      quantity: 1,
      error,
      isNetworkError: true
    })
    console.error(error)
    return null
  }
}

async function buyNowWithItems(items) {
  const cartResponse = await fetchShopifyGraphQL({
    query: CREATE_CART_GQL,
    variables: buildBuyNowVariables(items)
  })
  const cartCreate = cartResponse.data?.cartCreate
  const errors = [...(cartResponse.errors || []), ...(cartCreate?.userErrors || [])]
  const checkoutUrl = cartCreate?.cart?.checkoutUrl

  if (errors.length || !checkoutUrl) {
    const message = errors
      .map(error => error.message)
      .filter(Boolean)
      .join('; ')
    throw new Error(message || 'Unable to create checkout cart')
  }

  return checkoutUrl
}

function assignCartAddApi(Ctor) {
  if (typeof Ctor !== 'function' || Ctor.prototype.applyAddResponse) return

  Ctor.prototype.getSectionIds = function getSectionIds() {
    return getCartSectionIds(this)
  }

  Ctor.prototype.attachAddPayload = function attachAddPayload(payload) {
    return attachCartAddPayload(this, payload)
  }

  Ctor.prototype.applyAddResponse = function applyAddResponse(response, options) {
    return applyCartAddResponse(this, response, options)
  }
}

assignCartAddApi(typeof CartItems === 'function' ? CartItems : undefined)
assignCartAddApi(typeof CartDrawer === 'function' ? CartDrawer : undefined)

window.CartActions = {
  getCartComponent,
  addItemsToCart,
  buyNowWithItems
}
